import React, { Component } from "react";
import PropTypes from "prop-types";
import PluginPlaceholderVisor from "./VisorPluginPlaceholder";
import RecorderHtmlFrame from "./RecorderHtmlFrame";
import { withHtmlCsp, agentNavGuard, isAgentHtmlBox } from "../../../common/agentHtmlGuard";
import ScaledCanvasFrame from "../../../common/ScaledCanvasFrame";
import {
  isSortableBox,
  isAncestorOrSibling,
  isBox,
  isSortableContainer,
  isForeignOrigin,
} from "../../../common/utils";
import { isUnitlessNumber } from "../../../common/cssNonUnitProps";
import {
  safeDecodeURI,
  normalizeFontSizes,
  stripParagraphTags,
} from "../../../common/common_tools";
import { resolveBoxTiming, resolveBoxEndFrame } from "../../../common/resolveBoxTiming.es6";
import { sceneTimeBus } from "../../../common/scene_clock.es6";
import { AnimatedWrapper } from "../../../common/animation/AnimatedWrapper";
import {
  TypeWriterWrap,
  WavyWrap,
  TYPE_WRITER_NAMES,
  WAVY_NAMES,
} from "../../../common/animation/SpecialTextWrappers";

import DOMPurify from "dompurify";

import {
  textAnimationPresets as TEXT_ANIMATION_PRESETS,
  boxAnimationPresets as BOX_ANIMATION_PRESETS,
  boxExitAnimationPresets as BOX_EXIT_ANIMATION_PRESETS,
  normalizeAnimationName,
  _PAN_ENTRY_MAP,
  _PAN_EXIT_MAP,
  _resolvePanConfig,
} from "../../../common/animation/presets.es6";

// SEC-18 fix round 1: course-authored links may legitimately set target="_blank"
// (CKEditor's Link dialog Target tab is enabled by default — dist/lib/ckeditor/config.js
// only sets linkShowAdvancedTab=false, not linkShowTargetTab). DOMPurify's default
// profile strips `target`, which made every "open in new window" link navigate the
// Visor's own host-LMS iframe instead. Re-allowed only at the course-text render
// sinks below via SAFE_LINK_CONFIG, paired with a rel hook so a restored target
// can't be used for reverse tabnabbing.
const SAFE_LINK_CONFIG = { ADD_ATTR: ["target"] };
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && node.hasAttribute("target")) {
    node.setAttribute("rel", "noopener noreferrer");
  }
});

const ensureFinite = (value, defaultValue) => {
  const num = parseFloat(value);
  return typeof num === "number" && isFinite(num) ? num : defaultValue;
};

export default class VisorBox extends Component {
  constructor(props) {
    super(props);
    this.borderSize = 2;
    this.animationRef = React.createRef();
    this.contentRef = React.createRef();
    this.state = {
      show: this.props.show,
      animationKey: 0,
      boxAnimationKey: 0,
      exitPhaseActive: false,
      exitAnimationKey: 0,
    };
  }

  arraysEqual(arr1, arr2) {
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) return arr1 === arr2;
    if (arr1.length !== arr2.length) return false;
    return arr1.every((val, index) => val === arr2[index]);
  }

  componentDidUpdate(prevProps, prevState) {
    let toolbar = this.props.toolbars[this.props.id];
    if (!toolbar || !toolbar.pluginId) return;
    let pluginAPI = IdealStudio.Visor.Plugins[toolbar.pluginId];
    if (!pluginAPI || typeof pluginAPI.getConfig !== "function") {
      return;
    }
    let config = pluginAPI.getConfig(toolbar.pluginId);

    if (config.needsTextEdition && window.MathJax) {
      window.MathJax.Hub.Queue(["Typeset", window.MathJax.Hub]);
    }

    const animationKeyChanged = prevState.animationKey !== this.state.animationKey;
    const boxAnimationKeyChanged = prevState.boxAnimationKey !== this.state.boxAnimationKey;

    if ((animationKeyChanged || boxAnimationKeyChanged) && prevState.show === this.state.show) {
      return;
    }

    if (this.props.resetTriggered !== prevProps.resetTriggered && this.props.resetTriggered) {
      this.setState({
        animationKey: this.state.animationKey + 1,
        boxAnimationKey: this.state.boxAnimationKey + 1,
      });
      return;
    }

    const prevViewArray = Array.isArray(prevProps.currentView)
      ? prevProps.currentView
      : [prevProps.currentView];
    const currViewArray = Array.isArray(this.props.currentView)
      ? this.props.currentView
      : [this.props.currentView];
    const currentViewChanged = !this.arraysEqual(currViewArray, prevViewArray);
    const showChanged = !prevProps.show && this.props.show;

    let shouldUpdateAnimations = false;

    if (currentViewChanged) {
      const prevParent = prevViewArray[0];
      const currentParent = currViewArray[0];

      const wasInContainedView =
        prevViewArray.length > 1 &&
        typeof prevViewArray[1] === "string" &&
        prevViewArray[1].includes("cv-");
      const isReturningToSameParent = prevParent === currentParent;

      // Only reset animations if NOT returning from contained view to same parent
      if (!(wasInContainedView && isReturningToSameParent)) {
        shouldUpdateAnimations = true;
      }
    }

    if (showChanged && !shouldUpdateAnimations) {
      const wasVisorPaused = prevProps.visorPlaying === false;
      const isVisorResuming = this.props.visorPlaying === true;
      const isReturningFromMark = wasVisorPaused && isVisorResuming;

      // Only reset animations if NOT returning from mark content
      if (!isReturningFromMark) {
        shouldUpdateAnimations = true;
      }
    }

    // Check if text animation changed and trigger re-animation
    const currentBox = this.props.boxes[this.props.id];
    const prevBox = prevProps.boxes && prevProps.boxes[this.props.id];

    const textAnimationChanged =
      currentBox &&
      currentBox.textAnimation &&
      currentBox.textAnimation.animation &&
      (!prevBox ||
        !prevBox.textAnimation ||
        !prevBox.textAnimation.animation ||
        currentBox.textAnimation.animation.name !== prevBox.textAnimation.animation.name ||
        currentBox.textAnimation.duration !== prevBox.textAnimation.duration ||
        currentBox.textAnimation.delay !== prevBox.textAnimation.delay);

    const boxAnimationChanged =
      currentBox &&
      currentBox.boxAnimation &&
      currentBox.boxAnimation.animation &&
      (!prevBox ||
        !prevBox.boxAnimation ||
        !prevBox.boxAnimation.animation ||
        currentBox.boxAnimation.animation.name !== prevBox.boxAnimation.animation.name ||
        currentBox.boxAnimation.duration !== prevBox.boxAnimation.duration ||
        currentBox.boxAnimation.delay !== prevBox.boxAnimation.delay);

    if (shouldUpdateAnimations || textAnimationChanged || boxAnimationChanged) {
      if (shouldUpdateAnimations) this._exitActivatedByMedia = false;
      this.setState({
        animationKey:
          shouldUpdateAnimations || textAnimationChanged
            ? this.state.animationKey + 1
            : this.state.animationKey,
        boxAnimationKey:
          shouldUpdateAnimations || boxAnimationChanged
            ? this.state.boxAnimationKey + 1
            : this.state.boxAnimationKey,
        // Reset exit phase on navigation so element re-appears on next visit
        ...(shouldUpdateAnimations ? { exitPhaseActive: false } : {}),
      });
    }

    // Live playback checks exits from the committed frame (componentDidMount).
    if (this.props.recorderMode) this._checkExitAt(this.props.visorFrameTime || 0);

    // Media-driven exit trigger for videos: the frame clock freezes at the
    // scene's durationFrames while an endFrame-less video keeps playing, so a
    // frame-based exitStartFrame can be unreachable. The <video>'s own clock
    // isn't frozen — trigger on "remaining playtime <= exit duration".
    this._syncVideoExitWatch();
  }

  // Exit window ends at min(own end, scene end). Functional setState: a new
  // visit's reset may still be pending.
  _checkExitAt(currentFrame) {
    const currentBox = this.props.boxes[this.props.id];
    const exitAnim = currentBox?.boxExitAnimation;
    if (!exitAnim?.animation) return;
    const FPS = 30;
    const exitDurationFrames = Math.round((exitAnim.duration || 1) * FPS);
    const sceneEndFrame = IdealStudio.State.navItemsById?.[currentBox.parent]?.duration || 0;
    const legacyAnims = IdealStudio.State.animationsById?.[this.props.id] || [];
    const ownEnd = resolveBoxEndFrame(currentBox, this._videoMediaFrames(FPS), legacyAnims, FPS);
    const rawEndFrame = typeof ownEnd === "number" && ownEnd > 0 ? ownEnd : 0;
    const endFrame =
      sceneEndFrame > 0
        ? Math.min(rawEndFrame > 0 ? rawEndFrame : sceneEndFrame, sceneEndFrame)
        : rawEndFrame;
    // No known end: anchoring at 0 would fire at once.
    if (!(endFrame > 0)) return;
    const exitStartFrame = Math.max(0, endFrame - exitDurationFrames);
    const hasExitStarted = currentFrame >= exitStartFrame;
    this.setState((st) => {
      if (hasExitStarted && !st.exitPhaseActive) {
        return { exitPhaseActive: true, exitAnimationKey: st.exitAnimationKey + 1 };
      }
      if (!hasExitStarted && st.exitPhaseActive && !this._exitActivatedByMedia) {
        // Seek back before the exit; media-triggered exits stay.
        return { exitPhaseActive: false };
      }
      return null;
    });
  }

  // Only frames of this box's own scene.
  _onCommittedFrame = () => {
    if (this.props.recorderMode) return;
    const cv = this.props.currentView;
    const sceneId = Array.isArray(cv) ? cv[cv.length - 1] : cv;
    if (sceneTimeBus.frameSceneId !== sceneId) return;
    this._checkExitAt(sceneTimeBus.frame);
  };

  // Attach (once) a timeupdate listener that activates the exit phase when the
  // video's remaining playtime <= exit duration. Live playback only (recorder
  // drives exits via seekTimeSec); looping videos skipped.
  _syncVideoExitWatch() {
    if (this.props.recorderMode) return;
    if (this._videoExitWatchEl) return; // already attached
    const box = this.props.boxes[this.props.id];
    const exitAnim = box?.boxExitAnimation;
    if (!exitAnim?.animation) return;
    if (this.props.toolbars?.[this.props.id]?.pluginId !== "EnrichedPlayer") return;
    const videoEl = document.querySelector(`#box-${this.props.id} video`);
    if (!videoEl) return;
    this._videoExitWatchEl = videoEl;
    this._onVideoExitTimeUpdate = () => {
      if (videoEl.loop) return;
      const d = videoEl.duration;
      if (!Number.isFinite(d) || d <= 0) return;
      const exitDurSec = exitAnim.duration || 1;
      if (!videoEl.paused && d - videoEl.currentTime <= exitDurSec && !this.state.exitPhaseActive) {
        this._exitActivatedByMedia = true;
        this.setState({
          exitPhaseActive: true,
          exitAnimationKey: this.state.exitAnimationKey + 1,
        });
      }
    };
    videoEl.addEventListener("timeupdate", this._onVideoExitTimeUpdate);
  }

  componentWillUnmount() {
    if (this._offCommittedFrame) this._offCommittedFrame();
    this._offCommittedFrame = null;
    if (this._videoExitWatchEl && this._onVideoExitTimeUpdate) {
      this._videoExitWatchEl.removeEventListener("timeupdate", this._onVideoExitTimeUpdate);
      this._videoExitWatchEl = null;
      this._onVideoExitTimeUpdate = null;
    }
  }

  // This box's <video> media length in frames (null before metadata / non-video).
  // Cached — feeds resolveBoxEndFrame for videos with no timeline.endFrame.
  _videoMediaFrames(FPS) {
    const cached = this._videoMediaFramesCache;
    if (cached && cached.id === this.props.id && cached.value > 0) return cached.value;
    const videoEl = document.querySelector(`#box-${this.props.id} video`);
    if (!videoEl || !Number.isFinite(videoEl.duration) || videoEl.duration <= 0) return null;
    const value = Math.round(videoEl.duration * FPS);
    this._videoMediaFramesCache = { id: this.props.id, value };
    return value;
  }

  getTextContent(content) {
    // Extract text content from React element
    if (typeof content === "string") {
      return content;
    }
    if (React.isValidElement(content)) {
      // Check for dangerouslySetInnerHTML first
      if (content.props && content.props.dangerouslySetInnerHTML) {
        const htmlText = content.props.dangerouslySetInnerHTML.__html;
        // Strip HTML tags to get plain text
        // SEC-18 invariant: this innerHTML write is only safe because htmlText
        // was already produced by a DOMPurify.sanitize() call upstream (e.g.
        // renderChildren's dangerouslySetInnerHTML) — this function never
        // receives raw, unsanitized course text directly. `temp` is detached
        // and never appended, and only .textContent is read back (never HTML),
        // so this itself isn't a new sink; it inherits the upstream sanitize.
        const temp = document.createElement("div");
        temp.innerHTML = htmlText;
        return temp.textContent || temp.innerText || "";
      }

      // Recursively extract text from children
      const extractText = (element) => {
        if (typeof element === "string") return element;
        if (!element) return "";

        // Check for dangerouslySetInnerHTML in nested elements
        if (element.props && element.props.dangerouslySetInnerHTML) {
          // SEC-18 invariant: same as above — safe only because the upstream
          // sink already sanitized element.props.dangerouslySetInnerHTML.__html.
          const temp = document.createElement("div");
          temp.innerHTML = element.props.dangerouslySetInnerHTML.__html;
          return temp.textContent || temp.innerText || "";
        }

        if (element.props && element.props.children) {
          if (Array.isArray(element.props.children)) {
            return element.props.children.map(extractText).join("");
          }
          return extractText(element.props.children);
        }
        return "";
      };
      return extractText(content);
    }
    if (Array.isArray(content)) {
      return content.map((item) => this.getTextContent(item)).join("");
    }
    return "";
  }

  getHTMLContent(content) {
    // Extract HTML content while preserving styles
    if (typeof content === "string") {
      return { html: content, parentStyles: {} };
    }
    if (React.isValidElement(content)) {
      // Extract parent element styles - THIS includes font styles!
      const parentStyles = content.props && content.props.style ? content.props.style : {};

      // Check for dangerouslySetInnerHTML first
      if (content.props && content.props.dangerouslySetInnerHTML) {
        const htmlContent = content.props.dangerouslySetInnerHTML.__html;

        return {
          html: htmlContent,
          parentStyles: parentStyles,
        };
      }

      // If no dangerouslySetInnerHTML, try to extract from children
      // This happens with renderChildren path
      if (content.props && content.props.children) {
        // Look for HTML in nested children
        const findHTMLInChildren = (children) => {
          if (!children) return null;

          if (React.isValidElement(children)) {
            if (children.props && children.props.dangerouslySetInnerHTML) {
              return children.props.dangerouslySetInnerHTML.__html;
            }
            // Recursively check children
            return findHTMLInChildren(children.props.children);
          }

          if (Array.isArray(children)) {
            for (let child of children) {
              const html = findHTMLInChildren(child);
              if (html) return html;
            }
          }

          if (typeof children === "string") {
            return children;
          }

          return null;
        };

        const htmlContent = findHTMLInChildren(content.props.children);
        if (htmlContent) {
          return {
            html: htmlContent,
            parentStyles: parentStyles,
          };
        }
      }
    }
    return null;
  }

  /*
        if(prevProps.fromAnswer !== undefined && prevProps.fromAnswer) {
            interact(ReactDOM.findDOMNode(this))
                .draggable({
                inertia: true,
                modifiers: [
                    interact.modifiers.restrictRect({
                      restriction: '.dropzoneq',
                      endOnly: true
                    })
                  ],
                autoScroll: true,
                // dragMoveListener from the dragging demo above
                listeners: {
                     move: this.dragMoveListener ,
                     end (event) {
                        if(event.relatedTarget == null){

                        event.target.style.left = 0 + "px";
                        event.target.style.top = 0 + "px";

                        event.target.setAttribute('data-x', 0);
                        event.target.setAttribute('data-y', 0);

                        }
                      }

                    }
                });
        } */

  componentWillReceiveProps(nextProps) {
    if (!this.state.show && !this.props.show && nextProps.show) {
      this.setState({ show: true });
    }
  }
  onDropzoneClickHandler(event) {
    if (event.target.getAttribute("value") != null) {
      let relatedTarget = document.getElementById(event.target.getAttribute("value")).parentElement
        .parentElement.parentElement.parentElement;
      relatedTarget.style.left = 0 + "px";
      relatedTarget.style.top = 0 + "px";
      relatedTarget.setAttribute("data-x", 0);
      relatedTarget.setAttribute("data-y", 0);
      relatedTarget.style.pointerEvents = "auto";
      event.target.removeAttribute("value");
      let id = Number(event.target.id.replace("dropzone_", ""));
      this.props.removeAnswerForDnd({ id, ansval: -1 });
    } else {
      // TODO : alert
    }
  }

  fibVisorOnChangeHandler(event) {
    let id = Object.keys(this.props.boxes).find((e) => !e.includes("_"));
    let page = this.props.currentView;
    let value = event.target.value;
    let index = event.target.id.replace("fib-", "");
    this.props.setAnswerForFib({ id: index, ansval: value });
  }

  dragMoveListener(event) {
    let target = event.target;
    let x = (parseFloat(target.getAttribute("data-x")) || 0) + event.dx;
    let y = (parseFloat(target.getAttribute("data-y")) || 0) + event.dy;

    target.style.left = x + "px";
    target.style.top = y + "px";

    target.setAttribute("data-x", x);
    target.setAttribute("data-y", y);
  }

  _panExitPreset(box) {
    const anim = box.boxExitAnimation?.animation;
    const name = normalizeAnimationName(typeof anim === "string" ? anim : anim?.name);
    return _PAN_EXIT_MAP[name] ? BOX_EXIT_ANIMATION_PRESETS.find((p) => p.name === name) : null;
  }

  // The exit starts `duration` before the box end or the scene end, whichever is first.
  _exitStartFrame(box, exitPreset, legacyAnims) {
    const FPS = 30;
    const exitDur = ensureFinite(
      box.boxExitAnimation.duration ?? exitPreset.animation.transition.duration,
      1
    );
    const sceneEnd = IdealStudio.State.navItemsById?.[box.parent]?.duration || 0;
    const ownEnd = resolveBoxEndFrame(box, this._videoMediaFrames(FPS), legacyAnims, FPS);
    const rawEnd = typeof ownEnd === "number" && ownEnd > 0 ? ownEnd : null;
    const cappedEnd =
      sceneEnd > 0 ? Math.min(rawEnd != null ? rawEnd : sceneEnd, sceneEnd) : rawEnd;
    return cappedEnd != null ? cappedEnd - Math.round(exitDur * FPS) : null;
  }

  render() {
    if (!this.state.show) {
      return null;
    }
    let cornerSize = 15;
    let box = this.props.boxes[this.props.id];
    let toolbar = this.props.toolbars[this.props.id];
    if (!toolbar || !toolbar.pluginId) {
      return null;
    }
    let pluginAPI = IdealStudio.Visor.Plugins[toolbar.pluginId];
    if (!pluginAPI || typeof pluginAPI.getConfig !== "function") {
      console.error(
        "[Visor] Plugin not registered for toolbar.pluginId=",
        toolbar.pluginId,
        "(box id:",
        this.props.id,
        "). Skipping render."
      );
      return null;
    }
    let config = pluginAPI.getConfig(toolbar.pluginId);
    let style = {};

    // ponytail: mirrors EditorBox.isNestedInAssessment() — true when this box is
    // a question/answer nested inside an assessment (evaluation-category) box.
    // Used below to scale textFontSize with the canvas em base instead of px.
    let isNestedInAssessment = false;
    if (isBox(box.parent)) {
      const parentTb = this.props.toolbars[box.parent];
      const parentPlugin = parentTb && IdealStudio.Visor.Plugins[parentTb.pluginId];
      isNestedInAssessment = !!(parentPlugin && parentPlugin.getConfig().category === "evaluation");
    }

    const isPaused = this.props.visorPlaying === false || this.props.sceneAdvancing === false;

    // animDelay is always 0 when timing is controlled by timeline.startFrame (mount gate).
    // resolveBoxTiming mirrors the same logic used in buildLayers.js and VisorCanvasSli.
    const _legacyAnimsForBox = IdealStudio.State.animationsById?.[this.props.id] || [];
    const _resolved = resolveBoxTiming(box, _legacyAnimsForBox);
    const { animDelay: resolvedAnimDelay } = _resolved;

    // Recorder mode: derive a deterministic per-box "time-since-mount" from
    // visorFrameTime so AnimatedWrapper can interpolate Framer Motion
    // styles by frame instead of wall-clock. The seek loop runs much faster
    // than wall-clock would, so FM-driven animations finish in the first
    // few captured frames if left to wall-clock.
    const FPS_REC = 30;
    const _isRecorder = this.props.recorderMode === true;
    const _boxStartFrame = typeof _resolved.startFrame === "number" ? _resolved.startFrame : 0;
    // Box-relative scene time; live playback also follows sceneTimeBus.
    const _animTimeSec = Math.max(0, ((this.props.visorFrameTime || 0) - _boxStartFrame) / FPS_REC);
    const _liveOriginSec = _isRecorder ? null : _boxStartFrame / FPS_REC;
    const _liveSceneId = _isRecorder ? null : this.props.currentView;
    let attrs = {};
    let classNames = "";

    let width = toolbar.structure.width;
    let height = toolbar.structure.height;
    let widthUnit = toolbar.structure.widthUnit;
    let heightUnit = toolbar.structure.heightUnit;
    if (toolbar.state.__text) {
      // Match the editor: inside a sortable container the text inherits the
      // container's alignment (Settings → Structure "Alignment"); standalone
      // text stays left. Per-box alignment still wins via boxTextAlign-*.
      style.textAlign = isSortableContainer(box.container) ? "inherit" : "left";
    }

    // pass currentState  of component if exists
    if (this.props.richElementsState && this.props.richElementsState[box.id] !== undefined) {
      toolbar.state.currentState = this.props.richElementsState[box.id];
    }

    let rotate = "rotate(0deg)";
    if (toolbar.structure.rotation && toolbar.structure.rotation) {
      rotate = "rotate(" + toolbar.structure.rotation + "deg)";
    }

    if (toolbar.structure.horizontalFlip) {
      rotate += " scaleX(-1)";
    }

    if (toolbar.structure.verticalFlip) {
      rotate += " scaleY(-1)";
    }

    // style.transform = style.WebkitTransform = style.MsTransform = rotate;
    let toolbarStyle = {};
    for (let propCSS in toolbar.style) {
      if (!isNaN(toolbar.style[propCSS]) && isUnitlessNumber.indexOf(propCSS) === -1) {
        toolbarStyle[propCSS] = toolbar.style[propCSS] / 7 + "em";
      } else {
        toolbarStyle[propCSS] = toolbar.style[propCSS];
      }
    }
    style = { ...style, ...toolbarStyle };

    style.width = "100%";
    style.height = "100%";

    /* TODO: Reassign object if it's rich to have marks as property box.content.props*/
    let marks = {};
    Object.keys(this.props.marks || {}).forEach((mark) => {
      if (this.props.marks[mark].origin === this.props.id) {
        marks[mark] = this.props.marks[mark];
      }
    });
    let props = {
      ...this.props,
      visor: true,
      visorCurrentTime: _animTimeSec,
      animationTimeOriginSec: _liveOriginSec,
      animationSceneId: _liveSceneId,
      onAdvance: () => {
        try {
          window.dispatchEvent(new CustomEvent("aidea:advance-scene"));
        } catch (_) {
          const ev = document.createEvent("CustomEvent");
          ev.initCustomEvent("aidea:advance-scene", true, true, null);
          window.dispatchEvent(ev);
        }
      },
      onNavigate: (toSceneId) => {
        if (!toSceneId) return;
        try {
          window.dispatchEvent(new CustomEvent("aidea:advance-scene", { detail: { toSceneId } }));
        } catch (_) {
          const ev = document.createEvent("CustomEvent");
          ev.initCustomEvent("aidea:advance-scene", true, true, { toSceneId });
          window.dispatchEvent(ev);
        }
      },
      parentBox: this.props.boxes[this.props.id],
      marks,
      allMarks: this.props.marks,
      setAnswer: (correctAnswer) => {
        this.props.setAnswer(this.props.id, correctAnswer, this.props.currentView);
      },
      setAnswerForFib: (correctAnswer) => {
        this.props.setAnswer(this.props.id, correctAnswer, this.props.currentView, true);
      },
      removeAnswerForDnd: (answer) => {
        this.props.setAnswer(this.props.id, answer, this.props.currentView, true);
      },
      tryAgainHandler: () => {
        this.props.tryAgainHandler(this.props.id, this.props.currentView);
      },
      seeAnswerHandler: () => {
        this.props.seeAnswerHandler(this.props.id, this.props.currentView);
      },
      submitPage: (page, questionId) => {
        // Scope the submit to THIS exercise by default. Without forwarding a
        // questionId, a per-exercise "Check Answer" submitted the whole scene —
        // flipping every other exercise to attempted (Try Again / feedback on
        // questions the learner never touched).
        this.props.submitPage(page || this.props.currentView, questionId || this.props.id);
      },
      checkAnswerForFreeResponse: () => {
        this.props.checkAnswerForFreeResponse(this.props.currentView, this.props.id);
      },
    };

    let content;
    const isHtmlState = toolbar.state._html !== undefined && toolbar.state._html !== null;

    if (isHtmlState) {
      let htmlValue = toolbar.state._html;
      const isUrl =
        typeof htmlValue === "string" &&
        (htmlValue.startsWith("http://") || htmlValue.startsWith("https://"));
      const isFullHtmlDoc =
        typeof htmlValue === "string" &&
        (htmlValue.includes("<script") || htmlValue.includes("<html"));

      if (isUrl) {
        content = (
          <div
            style={{ ...style, padding: 0, overflow: "hidden" }}
            {...attrs}
            className={"boxStyle " + classNames}
            ref={this.contentRef}
          >
            <iframe
              src={htmlValue}
              style={{ width: "100%", height: "100%", border: "none", display: "block" }}
              sandbox={
                // allow-same-origin only for foreign origins: a same-origin author URL
                // would otherwise get full access to the host page/session.
                isForeignOrigin(htmlValue)
                  ? "allow-scripts allow-same-origin allow-popups"
                  : "allow-scripts allow-popups"
              }
              title="HTML content"
            />
          </div>
        );
      } else if (isFullHtmlDoc) {
        let secureHtmlValue = htmlValue;
        // Apply strict CSP and tight sandbox to untrusted/custom HTML
        secureHtmlValue = withHtmlCsp(this.props.id, htmlValue);
        const sandboxRules = "allow-scripts";

        content = (
          <div
            style={{ ...style, padding: 0, overflow: "hidden" }}
            {...attrs}
            className={"boxStyle " + classNames}
            ref={this.contentRef}
          >
            <ScaledCanvasFrame>
              {_isRecorder && isAgentHtmlBox(this.props.id) ? (
                <RecorderHtmlFrame
                  srcDoc={secureHtmlValue}
                  startSec={_boxStartFrame / FPS_REC}
                  sandbox={sandboxRules}
                  onLoad={agentNavGuard(this.props.id, secureHtmlValue)}
                  style={{ width: "100%", height: "100%", border: "none", display: "block" }}
                />
              ) : (
                <iframe
                  srcDoc={secureHtmlValue}
                  style={{ width: "100%", height: "100%", border: "none", display: "block" }}
                  sandbox={sandboxRules}
                  onLoad={agentNavGuard(this.props.id, secureHtmlValue)}
                  title="HTML content"
                />
              )}
            </ScaledCanvasFrame>
          </div>
        );
      } else {
        const secureHtmlValue = DOMPurify.sanitize(htmlValue);
        content = (
          <div
            style={{ ...style, padding: 0, overflow: "auto", width: "100%", height: "100%" }}
            {...attrs}
            className={"boxStyle " + classNames}
            ref={this.contentRef}
            dangerouslySetInnerHTML={{ __html: secureHtmlValue }}
          />
        );
      }
    } else {
      content =
        config.flavor === "react" ? (
          <div
            style={style}
            {...attrs}
            className={
              "boxStyle " +
              classNames +
              (toolbar.state.textAlign ? " boxTextAlign-" + toolbar.state.textAlign : "")
            }
            ref={this.contentRef}
          >
            {pluginAPI.getRenderTemplate(toolbar.state, props)}
          </div>
        ) : (
          <div
            style={{
              ...style,
              // Mirror the editor's BasicText quick text-size scale so the learner
              // sees the same size (content inherits this %).
              ...(toolbar.state.fontScale
                ? { fontSize: Math.round(toolbar.state.fontScale * 100) + "%" }
                : {}),
              // Mirror the rest of the BasicText pill's box-level formatting
              // (font family/size/color/highlight/bold/italic/underline/align)
              // — same fields EditorBox.jsx applies to its view-mode wrapper —
              // so the learner-facing Visor (and the editor's Preview, which
              // renders through this same component) shows what was set.
              ...(toolbar.state.textAlign ? { textAlign: toolbar.state.textAlign } : {}),
              ...(toolbar.state.textFontFamily ? { fontFamily: toolbar.state.textFontFamily } : {}),
              // All text scales with the canvas em base (matches EditorBox —
              // standalone boxes dropped literal px so editor, CKEditor and
              // Visor all size text against the same reference).
              ...(toolbar.state.textFontSize
                ? { fontSize: toolbar.state.textFontSize / 14 + "em" }
                : {}),
              ...(toolbar.state.textColor ? { color: toolbar.state.textColor } : {}),
              ...(toolbar.state.highlightColor
                ? { backgroundColor: toolbar.state.highlightColor }
                : {}),
              ...(toolbar.state.bold ? { fontWeight: "bold" } : {}),
              ...(toolbar.state.italic ? { fontStyle: "italic" } : {}),
              ...(toolbar.state.underline ? { textDecoration: "underline" } : {}),
            }}
            {...attrs}
            className={
              "boxStyle " +
              classNames +
              (toolbar.state.textAlign ? " boxTextAlign-" + toolbar.state.textAlign : "")
            }
            ref={this.contentRef}
          >
            {this.renderChildren(
              IdealStudio.Visor.Plugins.get(toolbar.pluginId).export(
                toolbar.state,
                toolbar.pluginId,
                box.children.length !== 0,
                this.props.id
              ),
              0
            )}
          </div>
        );
    }

    if (this.props.isDndQuestion !== undefined && this.props.isDndQuestion) {
      // var text = toolbar.state.__text;
      var parts = safeDecodeURI(toolbar.state.__text).split(/{{(.*?)}}/);
      let temp = document.createElement("div");
      temp.innerHTML = DOMPurify.sanitize(safeDecodeURI(toolbar.state.__text));

      let all = temp.getElementsByTagName("*");
      let tags = [];
      for (var i = 0, max = all.length; i < max; i++) {
        let tagname = all[i].tagName;
        if (tags.indexOf(tagname) == -1) {
          tags.push(tagname);
        }
      }

      // <p> => <span>
      for (var i = 0; i < parts.length; i += 2) {
        if (parts[i] && typeof parts[i] === "string" && parts[i].trim()) {
          let htmlContent = parts[i]
            .replace(/<p\s*[^>]*>/gi, "<span>")
            .replace(/<\/p>/gi, "</span>");
          parts[i] = (
            <span
              key={"text-" + i}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(htmlContent, SAFE_LINK_CONFIG),
              }}
              style={{ marginBottom: "15px" }}
            />
          );
        }
      }

      for (var i = 1; i < parts.length; i += 2) {
        parts[i] = (
          <span
            key={"dropzone-" + (i - 1) / 2}
            style={{
              margin: "0 0 -15px 0",
              cursor: "pointer",
              display: "inline-block",
            }}
            className="dropzoneq"
            id={"dropzone_" + (i - 1) / 2}
            onClick={(e) =>
              this.props.isQuestionAttempted !== undefined && !this.props.isQuestionAttempted
                ? this.onDropzoneClickHandler(e)
                : null
            }
          ></span>
        );
      }
      content = parts;
    } else if (this.props.isFibQuestion !== undefined && this.props.isFibQuestion) {
      var parts = stripParagraphTags(safeDecodeURI(toolbar.state.__text)).split(/#----(.*?)----#/);
      for (var i = 1; i < parts.length; i += 1) {
        if (i % 2 == 0) {
          parts[i] = parts[i];
        } else {
          parts[i] = (
            <input
              type="text"
              // width:10% made the blank unusably narrow; give it a comfortable
              // fillable size that still flows inline with the question text.
              style={{
                margin: "4px 6px",
                minWidth: "120px",
                padding: "6px 10px",
                border: "1px solid #b6b8c3",
                borderRadius: "6px",
                fontSize: "inherit",
                verticalAlign: "middle",
              }}
              className="fibinput"
              id={"fib-" + (i - 1) / 2}
              aria-label={"Blank " + ((i - 1) / 2 + 1)}
              disabled={
                this.props.isQuestionAttempted !== undefined && this.props.isQuestionAttempted
              }
              onChange={(e) => this.fibVisorOnChangeHandler(e)}
            />
          );
        }
      }
      content = parts;
    }

    let classes = "wholeboxvisor";
    if (box.container) {
      classes += " dnd" + box.container;
    }

    if (box.height === "auto") {
      classes += " automaticallySizedBox";
    }

    let verticalAlign = "top";

    let wholeBoxVisorStyle = {
      position: box.position.type,
      left: box.position.x ? box.position.x : "",
      top: box.position.y ? box.position.y : "",
      width: width !== "auto" ? width + widthUnit : isNestedInAssessment ? "100%" : "max-content",
      maxWidth: width === "auto" ? "100%" : undefined,
      height: height !== "auto" ? height + heightUnit : "auto",
      verticalAlign: verticalAlign,
      transformOrigin: "center",
    };
    if (this.props.prewarm) {
      // Mounted ahead of its start frame: loaded but not shown.
      wholeBoxVisorStyle.visibility = "hidden";
      wholeBoxVisorStyle.pointerEvents = "none";
    }

    wholeBoxVisorStyle.transform =
      wholeBoxVisorStyle.WebkitTransform =
      wholeBoxVisorStyle.MsTransform =
        rotate;

    // textAnimation artık kullanılmıyor — boxAnimation'a migrate edildi
    const textAnimation = box.boxAnimation ? null : box.textAnimation;

    let wrappedContent = content;

    if (textAnimation && textAnimation.animation && !isHtmlState) {
      // Find the matching animation preset
      const animationPreset = TEXT_ANIMATION_PRESETS.find(
        (preset) => preset.name === textAnimation.animation.name
      );

      if (animationPreset) {
        // Simple approach: just wrap the content with animation
        // This preserves all CKEditor formatting exactly as it is
        const animationProps = {
          initial: animationPreset.animation.initial,
          animate: animationPreset.animation.animate,
          transition: {
            ...animationPreset.animation.transition,
            duration: textAnimation.duration || animationPreset.animation.transition.duration,
            delay: resolvedAnimDelay,
          },
        };

        // Special styles for specific animations - always include width/height to maintain layout
        let wrapperStyle = { width: "100%", height: "100%" };
        if (animationPreset.name === "Type Writer") {
          wrapperStyle = {
            ...wrapperStyle,
            whiteSpace: "nowrap",
            overflow: "hidden",
          };
        }

        // For wavy animation, preserve HTML styling while animating character by character
        if (animationPreset.animation.isWavy || animationPreset.name === "Type Writer") {
          const Wrapper = animationPreset.animation.isWavy ? WavyWrap : TypeWriterWrap;
          wrappedContent = (
            <Wrapper
              duration={animationProps.transition.duration}
              delay={resolvedAnimDelay}
              isPaused={isPaused}
              seekTimeSec={_animTimeSec}
              timeOriginSec={_liveOriginSec}
              sceneId={_liveSceneId}
              style={{ width: "100%", height: "100%" }}
            >
              {content}
            </Wrapper>
          );
        } else {
          // All other animations - wrap content and preserve everything with pause and play support
          wrappedContent = (
            <AnimatedWrapper
              key={`text-anim-${this.props.id}-${this.state.animationKey}`}
              initial={animationProps.initial}
              animateTarget={animationProps.animate}
              transition={animationProps.transition}
              style={wrapperStyle}
              isPaused={isPaused}
              seekTimeSec={_animTimeSec}
              timeOriginSec={_liveOriginSec}
              sceneId={_liveSceneId}
            >
              {content}
            </AnimatedWrapper>
          );
        }
      }
    }

    // Apply box animations to the entire container
    const boxAnimation = box.boxAnimation;

    let finalContent = wrappedContent;

    if (boxAnimation && boxAnimation.animation) {
      const _boxAnimRawName =
        typeof boxAnimation.animation === "string"
          ? boxAnimation.animation
          : boxAnimation.animation?.name;
      const _boxAnimResolved = normalizeAnimationName(_boxAnimRawName);
      const boxAnimationPreset = BOX_ANIMATION_PRESETS.find(
        (preset) => preset.name === _boxAnimResolved
      );

      if (boxAnimationPreset) {
        const boxAnimationProps = {
          initial: boxAnimationPreset.animation.initial,
          animate: boxAnimationPreset.animation.animate,
          transition: {
            ...boxAnimationPreset.animation.transition,
            duration: boxAnimation.duration || boxAnimationPreset.animation.transition.duration,
            delay: resolvedAnimDelay,
          },
        };
        const _isAtomicBox = toolbar?.pluginId === "AtomicBoxPlugin";
        const _boxAnimDur = boxAnimation.duration || boxAnimationProps.transition.duration;
        const _panEntry = _PAN_ENTRY_MAP[boxAnimationPreset.name];

        if (WAVY_NAMES.has(boxAnimationPreset.name)) {
          finalContent = (
            <WavyWrap
              duration={_boxAnimDur}
              delay={resolvedAnimDelay}
              isPaused={isPaused}
              seekTimeSec={_animTimeSec}
              timeOriginSec={_liveOriginSec}
              sceneId={_liveSceneId}
              style={{ width: "100%", height: "100%" }}
              animationKey={`${this.props.id}-box-${this.state.boxAnimationKey}`}
              reverse={false}
            >
              {wrappedContent}
            </WavyWrap>
          );
        } else if (TYPE_WRITER_NAMES.has(boxAnimationPreset.name)) {
          finalContent = (
            <TypeWriterWrap
              key={`box-anim-${this.props.id}-${this.state.boxAnimationKey}`}
              duration={_boxAnimDur}
              delay={resolvedAnimDelay}
              isPaused={isPaused}
              seekTimeSec={_animTimeSec}
              timeOriginSec={_liveOriginSec}
              sceneId={_liveSceneId}
              reverse={false}
              style={_isAtomicBox ? undefined : { width: "100%", height: "100%" }}
            >
              {wrappedContent}
            </TypeWriterWrap>
          );
        } else if (_panEntry) {
          // Pan (Ken Burns): outer box overflow:hidden + scale on a transform-origin
          // matching the editor's animationHelpers.js _applyPan behaviour.
          const _panAnimObj =
            typeof boxAnimation.animation === "object" ? boxAnimation.animation : boxAnimation;
          const pan = _resolvePanConfig(_panEntry, _panAnimObj);
          wholeBoxVisorStyle.overflow = "hidden";
          // A Pan exit replaces the Pan entry instead of multiplying its scale.
          let _releaseAtSec = null;
          const _panExitPreset = this._panExitPreset(box);
          if (_panExitPreset) {
            if (this._exitActivatedByMedia) {
              _releaseAtSec = this.state.exitPhaseActive ? 0 : null;
            } else {
              const f = this._exitStartFrame(box, _panExitPreset, _legacyAnimsForBox);
              if (f != null) _releaseAtSec = (f - _boxStartFrame) / FPS_REC;
            }
          }
          finalContent = (
            <AnimatedWrapper
              key={`box-anim-${this.props.id}-${this.state.boxAnimationKey}`}
              initial={pan.initial}
              animateTarget={pan.animate}
              transition={boxAnimationProps.transition}
              style={{ width: "100%", height: "100%", transformOrigin: pan.transformOrigin }}
              releaseAtSec={_releaseAtSec}
              isPaused={isPaused}
              seekTimeSec={_animTimeSec}
              timeOriginSec={_liveOriginSec}
              sceneId={_liveSceneId}
            >
              {wrappedContent}
            </AnimatedWrapper>
          );
        } else {
          let wrapperStyle = { width: "100%", height: "100%" };
          if (boxAnimationPreset.name === "Type Writer") {
            wrapperStyle = { ...wrapperStyle, whiteSpace: "nowrap", overflow: "hidden" };
          }

          finalContent = (
            <AnimatedWrapper
              key={`box-anim-${this.props.id}-${this.state.boxAnimationKey}`}
              initial={boxAnimationProps.initial}
              animateTarget={boxAnimationProps.animate}
              transition={boxAnimationProps.transition}
              style={wrapperStyle}
              isPaused={isPaused}
              seekTimeSec={_animTimeSec}
              timeOriginSec={_liveOriginSec}
              sceneId={_liveSceneId}
            >
              {wrappedContent}
            </AnimatedWrapper>
          );
        }
      }
    }

    // Exit animation. The wrapper stays mounted so starting the exit never
    // remounts the entry wrapper or the video.
    const boxExitAnimation = box.boxExitAnimation;
    if (boxExitAnimation?.animation) {
      const _exitAnimRawName =
        typeof boxExitAnimation.animation === "string"
          ? boxExitAnimation.animation
          : boxExitAnimation.animation?.name;
      const exitPreset = BOX_EXIT_ANIMATION_PRESETS.find(
        (preset) => preset.name === normalizeAnimationName(_exitAnimRawName)
      );
      if (exitPreset) {
        const _exitDur = boxExitAnimation.duration || exitPreset.animation.transition.duration;

        // Seconds since the exit started (negative before it).
        let _exitRawSec = null;
        let _exitOriginSec = null;
        const exitStartFrame = this._exitStartFrame(box, exitPreset, _legacyAnimsForBox);
        if (exitStartFrame != null) {
          _exitRawSec = ((this.props.visorFrameTime || 0) - exitStartFrame) / FPS_REC;
          _exitOriginSec = exitStartFrame / FPS_REC;
        }
        // An exit triggered by the video's end runs on its own clock.
        const _exitLive = !_isRecorder && !this._exitActivatedByMedia;

        if (WAVY_NAMES.has(exitPreset.name) || TYPE_WRITER_NAMES.has(exitPreset.name)) {
          const Wrapper = WAVY_NAMES.has(exitPreset.name) ? WavyWrap : TypeWriterWrap;
          finalContent = (
            <Wrapper
              key={`box-exit-${this.props.id}`}
              active={this.state.exitPhaseActive}
              duration={_exitDur}
              delay={0}
              isPaused={isPaused}
              seekTimeSec={_isRecorder || _exitLive ? _exitRawSec : null}
              timeOriginSec={_exitLive ? _exitOriginSec : null}
              sceneId={_exitLive ? _liveSceneId : null}
              reverse
              style={{ width: "100%", height: "100%" }}
            >
              {finalContent}
            </Wrapper>
          );
        } else {
          const exitProps = {
            initial: exitPreset.animation.initial,
            animate: exitPreset.animation.animate,
            transition: {
              ...exitPreset.animation.transition,
              duration: boxExitAnimation.duration || exitPreset.animation.transition.duration,
            },
          };
          let exitWrapperStyle = exitPreset.animation.isTypeWriterOut
            ? { width: "100%", height: "100%", overflow: "hidden" }
            : { width: "100%", height: "100%" };

          // Pan Out (Ken Burns çıkış): outer box overflow:hidden + scale on a
          // transform-origin matching the editor's animationHelpers.js _applyPan.
          const _panExit = _PAN_EXIT_MAP[exitPreset.name];
          if (_panExit) {
            const _panAnimObj =
              typeof boxExitAnimation.animation === "object"
                ? boxExitAnimation.animation
                : boxExitAnimation;
            const pan = _resolvePanConfig(_panExit, _panAnimObj);
            exitProps.initial = pan.initial;
            exitProps.animate = pan.animate;
            exitWrapperStyle = { ...exitWrapperStyle, transformOrigin: pan.transformOrigin };
            wholeBoxVisorStyle.overflow = "hidden";
          }

          finalContent = (
            <AnimatedWrapper
              key={`box-exit-${this.props.id}`}
              active={this.state.exitPhaseActive}
              initial={exitProps.initial}
              animateTarget={exitProps.animate}
              transition={exitProps.transition}
              style={exitWrapperStyle}
              isPaused={isPaused}
              seekTimeSec={_isRecorder || _exitLive ? _exitRawSec : null}
              timeOriginSec={_exitLive ? _exitOriginSec : null}
              clearBeforeStart
              sceneId={_exitLive ? _liveSceneId : null}
            >
              {finalContent}
            </AnimatedWrapper>
          );
        }
      }
    }

    return (
      <div className={classes} id={"box-" + this.props.id} style={wholeBoxVisorStyle}>
        {finalContent}
      </div>
    );
  }

  __getMarkKeys(marks) {
    let markKeys = {};
    Object.keys(marks).map((mark) => {
      let inner_mark = marks[mark];
      let value = inner_mark.value.toString();
      markKeys[value] = inner_mark.connection;
    });
    return markKeys;
  }

  renderChildren(markup, key) {
    if (!markup) {
      console.warn("renderChildren: markup is null or undefined");
      return null;
    }

    let component;
    let props = {};
    let children = null;

    // Handle case where markup doesn't have node property but has tag (fallback from html2json)
    if (!markup.node && markup.tag) {
      markup.node = "element";
    }

    switch (markup.node) {
      case "element":
        if (markup.attr) {
          props = markup.attr;
        }
        props.key = key;
        if (markup.tag === "plugin") {
          component = PluginPlaceholderVisor;
          let resizable = markup.attr && markup.attr.hasOwnProperty("plugin-data-resizable");
          props = Object.assign({}, props, {
            pluginContainer: markup.attr["plugin-data-id"],
            resizable: resizable,
            parentBox: this.props.boxes[this.props.id],
            boxes: this.props.boxes,
            toolbars: this.props.toolbars,
            richElementsState: this.props.richElementsState,
            changeCurrentView: this.props.changeCurrentView,
            currentView: this.props.currentView,
          });
        } else if (markup.tag) {
          component = markup.tag;
          // Fix: Convert <p> to <div> for feedback containers to avoid nesting issues
          // <div> cannot be a descendant of <p>, so we convert p to div for feedback
          if (component === "p" && this.props.boxes && this.props.id) {
            let box = this.props.boxes[this.props.id];
            if (box && (box.container === "TrueFeedback" || box.container === "FalseFeedback")) {
              component = "div";
            }
          }
        } else {
          // No tag specified, use div as fallback
          component = "div";
        }
        break;
      case "text":
        // Text nodes carry authored rich text that may contain HTML entities:
        // leading/repeated spaces are stored as &nbsp;, & as &amp;, etc. Rendering
        // them as a React text child ESCAPES those entities, so the learner sees a
        // literal "&nbsp;" (e.g. mixed-content feedback like "&nbsp;…Doğru
        // <strong>Bakır</strong>", which html2json splits into siblings so the
        // single-text-child innerHTML fast path below is skipped). Parse them via
        // innerHTML instead — same content and trust level as that fast path.
        let textContent = markup.text || "";
        textContent = safeDecodeURI(textContent);
        component = "span";
        props = {
          key: key,
          dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(textContent, SAFE_LINK_CONFIG) },
        };
        break;
      case "root":
        component = "div";
        props = { style: { width: "100%", height: "100%" } };
        break;
      default:
        // Fallback for unknown node types or missing node property
        if (markup.tag) {
          component = markup.tag;
          if (!markup.node) {
            markup.node = "element";
          }
        } else {
          console.warn("renderChildren: Unknown node type or missing node/tag property:", markup);
          component = "div";
          props = { key: key };
        }
        break;
    }

    // Safety check: ensure component is defined before creating React element
    if (!component) {
      console.error("renderChildren: component is undefined for markup:", markup);
      return null;
    }

    Object.keys(props).forEach((prop) => {
      // Authored markup must never yield executable attributes: drop string on*
      // handlers (was `new Function(value)`), srcdoc, and javascript: URLs.
      const attrValue = props[prop];
      if (
        typeof attrValue === "string" &&
        (/^on/i.test(prop) ||
          prop.toLowerCase() === "srcdoc" ||
          (/^(href|src|action|formaction|xlink:href)$/i.test(prop) &&
            /^[\s\u0000-\u001f]*javascript:/i.test(attrValue)))
      ) {
        delete props[prop];
        return;
      }
      // Convert style to object - handle string, array, or object formats
      if (prop === "style") {
        let styleValue = props[prop];
        let styleObj = {};

        const parseStyleString = (styleString) => {
          const normalizedStyle = normalizeFontSizes(styleString);
          const parsed = {};
          normalizedStyle.split(";").forEach((rule) => {
            let trimmedRule = rule.trim();
            if (trimmedRule) {
              let [property, value] = trimmedRule.split(":").map((s) => s.trim());
              if (property && value) {
                let camelProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
                parsed[camelProperty] = value;
              }
            }
          });
          return parsed;
        };

        // If style is an array (html2json default format)
        if (Array.isArray(styleValue)) {
          styleValue = styleValue.join(" ");
          styleObj = parseStyleString(styleValue);
          props[prop] = styleObj;
        }
        // If style is a string
        else if (typeof styleValue === "string") {
          styleObj = parseStyleString(styleValue);
          props[prop] = styleObj;
        }
        // If style is already an object
        else if (
          typeof styleValue === "object" &&
          styleValue !== null &&
          !Array.isArray(styleValue)
        ) {
          const needsParsing = Object.values(styleValue).some(
            (val) => typeof val === "string" && val.includes(":")
          );

          if (needsParsing) {
            Object.keys(styleValue).forEach((styleKey) => {
              if (typeof styleValue[styleKey] === "string" && styleValue[styleKey].includes(":")) {
                const parsed = parseStyleString(styleValue[styleKey]);
                Object.assign(styleObj, parsed);
              } else if (isNaN(styleKey)) {
                // Non-numeric keys that are already valid style properties
                styleObj[styleKey] = styleValue[styleKey];
              }
            });
          } else {
            // Object with valid style properties, just clean numeric keys (React 19 fix)
            Object.keys(styleValue).forEach((styleKey) => {
              if (isNaN(styleKey)) {
                styleObj[styleKey] = styleValue[styleKey];
              }
            });
          }
          props[prop] = styleObj;
        }
      }
      // Convert 'class' to 'className'
      if (prop === "class") {
        props.className = props[prop];
        delete props[prop];
      }
    });

    // List of void/self-closing HTML elements that cannot have children
    const voidElements = [
      "area",
      "base",
      "br",
      "col",
      "embed",
      "hr",
      "img",
      "input",
      "link",
      "meta",
      "param",
      "source",
      "track",
      "wbr",
    ];

    // Check if the component is a void element
    const isVoidElement =
      typeof component === "string" && voidElements.includes(component.toLowerCase());

    if (markup.child && !isVoidElement) {
      if (markup.child.length === 1 && markup.child[0].node === "text") {
        // Try both decodeURI and decodeURIComponent, use the one that works
        let decodedText = markup.child[0].text;
        decodedText = safeDecodeURI(decodedText);

        props.dangerouslySetInnerHTML = {
          __html: DOMPurify.sanitize(decodedText, SAFE_LINK_CONFIG),
        };
      } else {
        children = [];
        markup.child.forEach((child, index) => {
          if (child) {
            let childKey = child.attr && child.attr.key ? child.attr.key : `child-${key}-${index}`;
            let renderedChild = this.renderChildren(child, childKey);
            if (renderedChild !== null && renderedChild !== undefined) {
              children.push(renderedChild);
            }
          }
        });
      }
    }

    // Final safety check before creating React element
    if (!component) {
      console.error("renderChildren: component is still undefined after switch:", markup);
      return null;
    }

    return React.createElement(component, props, isVoidElement ? null : children);
  }

  componentDidMount() {
    let toolbar = this.props.toolbars[this.props.id];
    if (toolbar && toolbar.pluginId) {
      let pluginAPI = IdealStudio.Visor.Plugins.get(toolbar.pluginId);
      if (pluginAPI && pluginAPI.afterRender && this.contentRef.current) {
        pluginAPI.afterRender(this.contentRef.current, toolbar.state);
      }
    }
    this._syncVideoExitWatch();
    if (!this.props.recorderMode) {
      this._offCommittedFrame = sceneTimeBus.subscribeFrame(this._onCommittedFrame);
      this._onCommittedFrame(); // may mount inside its exit
    }
  }
}

VisorBox.propTypes = {
  /**
   * Show the current view
   */
  show: PropTypes.bool,
  /**
   * Identificador de la caja
   */
  id: PropTypes.string.isRequired,
  /**
   * Object containing all created boxes (by id)
   */
  boxes: PropTypes.object.isRequired,
  /**
   * Cambia la vista actual
   */
  changeCurrentView: PropTypes.func.isRequired,
  /**
   * Diccionario que contiene todas las toolbars
   */
  toolbars: PropTypes.object,
  /**
   * Estado del plugin enriquecido en la transición
   */
  richElementsState: PropTypes.object,
  /**
   * Function for submitting a page Quiz
   */
  setAnswer: PropTypes.func,
  tryAgainHandler: PropTypes.func,
  seeAnswerHandler: PropTypes.func,
  submitPage: PropTypes.func,

  /**
   * Vista actual
   */
  currentView: PropTypes.any,
  /**
   * All marks
   */
  marks: PropTypes.object,
  /**
   * Function that triggers a mark
   */
  onMarkClicked: PropTypes.func,
  /**
   * Boolean that indicates when animations should be reset
   */
  resetTriggered: PropTypes.bool,
  /**
   * Boolean that indicates if visor is currently playing (controls animation pause/play)
   */
  visorPlaying: PropTypes.bool,
  /**
   * The scene clock really moves (play intent and no media wait); content
   * that runs on its own time pauses on false.
   */
  sceneAdvancing: PropTypes.bool,
};
